import { createSlice, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import to from 'await-to-js';
import { toast } from 'react-toastify';
import { RootState } from './store';
import Driver from '../Types/Driver';
import APIService from '../Utils/APIService';
import { showAddNewDriver, showEditDriver } from './app-ui.store';
import { getUserType } from './auth.store';
import { URLConfig } from '../Utils/features';
import { DEFAULT_PAGE_SIZE } from '../Utils/constants';

export interface DriverState {
  loading: boolean;
  error: boolean;
  drivers: Driver[];
  minimalDrivers: any[];
  minimalVehicles: any[];
  availableVehicles: any[];
  selectedVehicles: any;
  selectedDrivers: any;
  vehicleSelectionMode: 'single' | 'multi';
}

const initialState = {
  drivers: [],
  loading: false,
  error: false,
  minimalDrivers: [],
  minimalVehicles: [],
  selectedVehicles: {},
  availableVehicles: [],
  selectedDrivers: {},
  vehicleSelectionMode: 'multi',
} as DriverState;

export const getAllDrivers = createAsyncThunk<any, { page: number; page_size: number }>(
  'drivers/get-all',
  async (arg, { rejectWithValue, getState }) => {
    const userType = getUserType(getState() as RootState);
    const url = URLConfig.get_drivers[userType];
    const page = Math.floor(arg.page / arg.page_size) + 1;
    const [err, data] = await to(APIService.get(url, { params: { page, page_size: arg.page_size } }));
    if (err) {
      toast.error('Something went wrong!. Could not fetch the drivers');
      return rejectWithValue(err);
    }
    return data?.data;
  },
);

export const addNewDriver = createAsyncThunk<any, Driver>(
  'drivers/create-new',
  async (driver, { rejectWithValue, dispatch }) => {
    const [err, data] = await to(APIService.post('/user/driver', driver));
    if (err) {
      toast.error(`${err.message || 'Something went wrong!'}. Could not create the driver account`);
      return rejectWithValue(err);
    }
    toast.success('Successfully added the driver account');
    dispatch(showAddNewDriver(false));
    dispatch(getAllDrivers({ page: 0, page_size: DEFAULT_PAGE_SIZE }));
    return data?.data;
  },
);

export const editDriver = createAsyncThunk<any, Driver>(
  'driver/edit',
  async (driver, { rejectWithValue, dispatch }) => {
    const { driver_id, ...body } = driver as any;
    const [err, data] = await to(APIService.put(`/user/driver/${driver_id}`, body));
    if (err) {
      toast.error(`${err.message || 'Something went wrong!'}. Could not update the driver account`);
      return rejectWithValue(err);
    }
    toast.success('Successfully updated the driver account');
    dispatch(showEditDriver(null));
    dispatch(getAllDrivers({ page: 0, page_size: DEFAULT_PAGE_SIZE }));
    return data?.data;
  },
);

export const fetchMinimalLicenseAndVehicle = createAsyncThunk<any, any>(
  'driver-and-vehicles/get-minimals',
  async (_, { getState }) => {
    const userType = getUserType(getState() as RootState);
    const driverUrl = URLConfig.getMinimalDrivers[userType];
    const vehicleUrl = URLConfig.getMinimalVehicle[userType];
    const [err, data] = await to(
      Promise.all([
        APIService.get(driverUrl, { params: { page: 1, page_size: 100 } }),
        APIService.get(vehicleUrl, { params: { page: 1, page_size: 100 } }),
      ]),
    );
    if (err) {
      toast.error(`${err.message || 'Something went wrong!'}. Could not fetch License and Vehicle Details`);
      return { drivers: [], vehicles: [] };
    }
    const [drivers, vehicles] = data!;
    const driverBody = drivers?.data;
    const vehicleBody = vehicles?.data;

    const driverList = Array.isArray(driverBody) ? driverBody : driverBody?.data ?? [];
    const vehicleList = Array.isArray(vehicleBody) ? vehicleBody : vehicleBody?.data ?? [];

    return {
      drivers: driverList.filter((d: any) => d.active === 'active'),
      vehicles: vehicleList.filter((v: any) => v.status === 'active'),
    };
  },
);

export const fetchMinimalVehicle = createAsyncThunk<any, any>(
  'driver/get-minimals',
  async (_, { rejectWithValue, getState }) => {
    const userType = getUserType(getState() as RootState);
    const vehicleUrl = URLConfig.getMinimalVehicle[userType];
    if (!vehicleUrl) return rejectWithValue('Not available for this user type');
    const [err, data] = await to(APIService.get(vehicleUrl, { params: { page: 1, page_size: 100 } }));
    if (err) {
      toast.error(`${err.message || 'Something went wrong!'}. Could not fetch Vehicle Details`);
      return [];
    }
    const vehicleBody = data?.data;
    const vehicleList = Array.isArray(vehicleBody) ? vehicleBody : vehicleBody?.data ?? [];
    return vehicleList.filter((v: any) => v.status === 'active');
  },
);

export const updateDriverStatus = createAsyncThunk<any, { driverId: number; status: string }>(
  'driver/update-status',
  async ({ driverId, status }, { rejectWithValue, dispatch }) => {
    const [err, data] = await to(APIService.put(`/user/driver/${driverId}/status`, { status }));
    if (err) {
      toast.error(`${err.message || 'Something went wrong!'}. Could not update the status`);
      return rejectWithValue(err);
    }
    toast.success('Successfully status updated');
    dispatch(getAllDrivers({ page: 0, page_size: DEFAULT_PAGE_SIZE }));
    dispatch(fetchMinimalLicenseAndVehicle(null));
    return data?.data;
  },
);

export const driverSlice = createSlice({
  name: 'driver',
  initialState,
  reducers: {
    setSelectedVehicle: (state, { payload }) => {
      state.selectedVehicles[payload.id] = !!payload.selected;
    },
    setSelectedDrivers: (state, { payload }) => {
      state.selectedDrivers[payload.id] = !!payload.selected;
    },
    setVehicleSelectionMode: (state, { payload }) => {
      state.vehicleSelectionMode = payload;
    },
    clearSelectedDriversAndVehicles: (state) => {
      state.selectedVehicles = {};
      state.selectedDrivers = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllDrivers.pending, (store) => {
      store.loading = true;
      store.error = false;
    });
    builder.addCase(getAllDrivers.rejected, (store) => {
      store.loading = false;
      store.error = true;
    });
    builder.addCase(getAllDrivers.fulfilled, (store, { payload }) => {
      store.loading = false;
      store.drivers = Array.isArray(payload) ? payload : payload?.data ?? [];
    });

    builder.addCase(fetchMinimalLicenseAndVehicle.pending, (store) => {
      store.loading = true;
    });
    builder.addCase(fetchMinimalLicenseAndVehicle.rejected, (store) => {
      store.loading = false;
    });
    builder.addCase(fetchMinimalLicenseAndVehicle.fulfilled, (store, { payload }) => {
      store.loading = false;
      store.minimalDrivers = payload.drivers;
      store.minimalVehicles = payload.vehicles;
    });
    builder.addCase(fetchMinimalVehicle.pending, (store) => {
      store.loading = true;
    });
    builder.addCase(fetchMinimalVehicle.rejected, (store) => {
      store.loading = false;
    });
    builder.addCase(fetchMinimalVehicle.fulfilled, (store, { payload }) => {
      store.loading = false;
      store.availableVehicles = payload;
    });
  },
});

export const { setSelectedVehicle, setSelectedDrivers, setVehicleSelectionMode, clearSelectedDriversAndVehicles } =
  driverSlice.actions;

export const driverStore = (store: RootState): DriverState => store.driver;

export const getDriverLoading = createSelector(driverStore, (store) => store.loading);
export const getDrivers = createSelector(driverStore, (store) => store.drivers);
export const getMinimalDrivers = createSelector(driverStore, (store) => store.minimalDrivers);
export const getMinimalVehicles = createSelector(driverStore, (store) => store.minimalVehicles);
export const getSelectedVehicles = createSelector(driverStore, (store) => store.selectedVehicles);
export const getSelectedDrivers = createSelector(driverStore, (store) => store.selectedDrivers);
export const getVehicleSelectionMode = createSelector(driverStore, (store) => store.vehicleSelectionMode);
export const getAvailableVehicles = createSelector(driverStore, (store) => store.availableVehicles);
export const getSelectedVehiclesCapacity = createSelector(
  getSelectedVehicles,
  getMinimalVehicles,
  (vehicles, avaVehicle) =>
    avaVehicle.reduce((p, { capacity, plateNumber }) => (vehicles[plateNumber] ? Number(capacity) + p : p), 0),
);

export default driverSlice.reducer;
